import { forwardRef, useMemo, useRef } from 'react';
import {
    PlaceholderBackdrop,
    PlaceholderEditor,
    PlaceholderHighlight,
    PlaceholderInput,
} from './PlaceholderTextarea.StyledComponents';

// Same token shape the backend's placeholder validators match.
const PLACEHOLDER_SPLIT_PATTERN = /(\[\[\w+\]\])/g;

interface PlaceholderTextareaProps {
    id: string;
    'data-testid': string;
    value: string;
    onChange: (value: string) => void;
    placeholders: string[];
    rows?: number;
}

// A textarea can't style its own text, so recognized tokens are highlighted on a see-through layer behind it.
export const PlaceholderTextarea = forwardRef<HTMLTextAreaElement, PlaceholderTextareaProps>(
    ({ id, 'data-testid': testId, value, onChange, placeholders, rows }, ref) => {
        const backdropRef = useRef<HTMLDivElement>(null);

        const highlighted = useMemo(() => {
            const recognized = new Set(placeholders.map((placeholder) => `[[${placeholder}]]`));
            return value.split(PLACEHOLDER_SPLIT_PATTERN).map((part, index) =>
                recognized.has(part) ? (
                    // eslint-disable-next-line @eslint-react/no-array-index-key -- split segments are positional and can repeat
                    <PlaceholderHighlight key={index} data-testid={`${testId}-highlight`}>
                        {part}
                    </PlaceholderHighlight>
                ) : (
                    part
                ),
            );
        }, [value, placeholders, testId]);

        return (
            <PlaceholderEditor>
                <PlaceholderBackdrop ref={backdropRef} aria-hidden="true">
                    {highlighted}
                    {/* A trailing newline collapses in a div but not in a textarea, which would misalign the last line. */}
                    {value.endsWith('\n') ? ' ' : null}
                </PlaceholderBackdrop>
                <PlaceholderInput
                    ref={ref}
                    id={id}
                    data-testid={testId}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onScroll={(e) => {
                        if (backdropRef.current) {
                            backdropRef.current.scrollTop = e.currentTarget.scrollTop;
                        }
                    }}
                    rows={rows}
                    autoResize
                />
            </PlaceholderEditor>
        );
    },
);

PlaceholderTextarea.displayName = 'PlaceholderTextarea';
